/* eslint-disable no-restricted-globals -- this module is the single sanctioned localStorage
   boundary; every other file must go through the repository interfaces (architecture §18). */

import { STORAGE_NAMESPACE } from './keys';

/**
 * Thin, failure-tolerant wrapper around Web Storage.
 *
 * Private browsing, a full quota or a disabled storage partition must degrade to a working
 * app rather than a crash — but degrading must not also *lose* what is already saved. A quota
 * failure on one write is not a reason for a child's existing profile to disappear, so reads
 * keep going to the browser after writes stop working. Values written during a degraded
 * session live in an in-memory overlay that shadows storage for those keys only.
 *
 * Degradation is observable: `backend` reflects the live state and `subscribe` reports
 * changes, so the shell can tell the parent that progress is not being saved.
 */

export type StorageBackend =
  /** Reads and writes both reach the browser. */
  | 'local-storage'
  /**
   * Reads still reach the browser; new values are memory-only for the rest of this session.
   * Deletion is tracked separately and may still reach the browser — see `probeStorage`.
   */
  | 'read-only'
  /** No browser storage at all; everything is in memory for this session. */
  | 'memory';

export interface StorageLike {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * The bytes written over a key whose contents must not exist when deletion is unavailable.
 *
 * Fixed, module-level and payload-free by construction: it is the same string for every key,
 * every reason and every payload, so it can carry nothing about what it replaced. It is
 * deliberately *not* a Thrive envelope — `getRaw` reports it as absence and `keys()` hides it,
 * so a tombstone can never be mistaken for data or produce a diagnostic of its own.
 */
export const ERASURE_TOMBSTONE = '{"thriveErased":true}';

/** What physically happened to the bytes. A fixed enum, never a description of the payload. */
export type ErasureOutcome =
  /** The key is gone from the backend. */
  | 'removed'
  /** The key could not be deleted, so its bytes were replaced by `ERASURE_TOMBSTONE`. */
  | 'overwritten'
  /** Neither mechanism worked. The bytes are still there and erasure will be re-attempted. */
  | 'failed';

export interface EraseOptions {
  /**
   * Keep serving this session's overlay value for the key instead of tombstoning it in memory.
   *
   * Used by the sanitised-rewrite path: the caller has a clean projection to hand out for the
   * rest of the session, and the *stored* bytes are the ones that must be destroyed.
   */
  retainSessionValue?: boolean;
  /**
   * Only deletion finishes this erasure; a tombstone overwrite does not.
   *
   * The default is right for a governed key, whose name this build chose: replacing the value
   * with payload-free bytes destroys everything the entry could be hiding. It is wrong for a key
   * whose *name* is unvouched-for text, because `thrive.v1.data:image/png;base64,…` is persistent
   * forbidden data no matter what its value is (review R7-3). Such a key stays in
   * `pendingErasures` until it is actually gone.
   *
   * Sticky per key: once any call has asked for removal, every later `erase` of that key inherits
   * the requirement whatever its own options say, until a removal actually succeeds (R8-1).
   */
  requireRemoval?: boolean;
}

/**
 * The result of asking the backend what it physically holds under a key.
 *
 * Absence and failure used to share one `null`, and the pass read that `null` as "nothing here":
 * a single transient throw from `getItem` was enough to hide a contaminated governed key from
 * every launch that hit it (review R7-1). They are now separate answers, and `unreadable` is
 * treated fail-closed by every caller — an entry whose bytes cannot be inspected is erased rather
 * than assumed clean.
 */
export type PhysicalRead =
  { status: 'present'; value: string } | { status: 'absent' } | { status: 'unreadable' };

/**
 * The result of walking the namespace: what was found, and whether the walk finished (S1-4).
 *
 * `complete: false` means the backend refused to be enumerated, so the list is whatever could be
 * salvaged and says nothing at all about what else is stored. It is the enumeration twin of
 * `PhysicalRead`'s `unreadable`, and its callers treat it the same way — fail-closed.
 */
export interface NamespaceEnumeration {
  keys: string[];
  complete: boolean;
}

/**
 * How far a broken `length` is probed before the walk gives up.
 *
 * A backend that will not report its own size can still be asked for `key(0)`, `key(1)`, … and
 * that is worth doing: the split Codex found is exactly one where `length` throws and `key()`
 * works, and salvaging the names is what lets a forbidden key actually be destroyed rather than
 * merely reported as unknowable. The cap is what stops the salvage attempt becoming unbounded
 * work on a hostile handle.
 */
const ENUMERATION_PROBE_LIMIT = 1_024;

export interface KeyValueStore {
  /** Live status — re-read it, never snapshot it. */
  readonly backend: StorageBackend;
  /** Notifies on every backend change. Returns an unsubscribe function. */
  subscribe(listener: (backend: StorageBackend) => void): () => void;
  getRaw(key: string): string | null;
  /**
   * What the backend is physically holding, with no session state in the way.
   *
   * `getRaw` answers "what should this session see", which is the overlay first and the browser
   * second. Decontamination asks the opposite question — "what is physically stored" — and the
   * two answers differ exactly when it matters: a clean value written into the overlay by a
   * degraded session sits on top of the contaminated bytes that were never destroyed, and a
   * validator reading through the overlay declares the key healthy while the payload oracle
   * underneath survives every launch (review R6-3).
   *
   * Goes to the handle the store was given, never to the `readable` conclusion, for the same
   * reason `erase` does: a callback can corrupt that conclusion mid-sequence.
   */
  readPhysical(key: string): PhysicalRead;
  /**
   * Namespaced key *names* the backend physically holds. Overlay and tombstones are not
   * consulted, and neither are values.
   *
   * Enumeration is deliberately value-blind. Filtering tombstoned entries out here read the
   * residue of an erasure as a reason to skip the whole entry — but the entry is a name as well
   * as a value, and a name under our namespace is attacker-controlled text that persists on its
   * own (review R7-3). Deciding what a value means is the caller's job, once it knows whether the
   * name is one this build chose.
   *
   * Returns whether the walk *finished*, not only what it found (S1-4). Enumeration can fail —
   * `length` or `key(index)` throwing while reads and deletions still work — and a partial list
   * silently reported as the whole namespace is a fail-OPEN answer to the one question this call
   * exists to ask. The caller has to be able to tell "there is nothing else here" from "I could
   * not finish looking".
   */
  physicalKeys(): NamespaceEnumeration;
  /**
   * Whether this store was given a browser storage handle at all (S1-5).
   *
   * Deliberately *not* "is the backend healthy". `backend` is a live conclusion drawn from probes
   * and from failures during the session, and one transient `getItem` throw at startup is enough
   * to make a perfectly real backend report itself as `memory`. A caller deciding whether physical
   * bytes might exist must not consult a conclusion that can be wrong in the unsafe direction —
   * it must ask whether there is anywhere for bytes to be, which never changes.
   */
  readonly hasPhysicalStore: boolean;
  /** True when the value reached the browser; false when it is memory-only for this session. */
  setRaw(key: string, value: string): boolean;
  /** Ordinary deletion. Honours the capability flags and degrades to an in-memory tombstone. */
  remove(key: string): void;
  /**
   * Privacy erasure (architecture §19): destroys the bytes under a key, whatever it takes.
   *
   * Unlike `remove`, this never consults the capability flags to decide whether to try. Those
   * flags are mutable session state and a re-entrant `onWarning` can corrupt them mid-sequence
   * (review R4-4), so a physical `removeItem`, a readback and a tombstoning `setItem` are each
   * attempted directly against the backend in their own try/catch. No warning or backend
   * notification is emitted until the whole sequence has finished, so no callback can
   * interleave with it.
   */
  erase(key: string, options?: EraseOptions): ErasureOutcome;
  /**
   * Runs `fn` with every warning and backend notification held until it returns.
   *
   * `erase` defers its own callbacks, but a privacy-relevant *sequence* is usually wider than
   * one erasure: the sanitised-rewrite path writes first and only erases when that write failed,
   * so a throwing `onWarning` raised by the write used to escape before the erasure was reached
   * and leave the contaminated bytes in place (review R5-1). Wrapping the whole sequence closes
   * that seam for every such pairing at once rather than one call site at a time.
   *
   * Re-entrant: nested scopes (including an `erase` inside one) share a single flush at the
   * outermost exit. The flush happens in a `finally`, so a throwing `fn` still delivers.
   */
  withDeferredCallbacks<T>(fn: () => T): T;
  /** Keys whose erasure failed outright and will be retried. Never carries payload data. */
  pendingErasures(): string[];
  /** Namespaced keys only. */
  keys(): string[];
}

export interface KeyValueStoreOptions {
  storage?: StorageLike | null;
  onWarning?: (message: string, cause?: unknown) => void;
  /**
   * Runs once, synchronously, inside the store's construction deferral scope.
   *
   * The decontamination pass is wired here rather than after the factory returns so that the
   * probe's own warnings and everything the pass emits share **one** queue and **one** flush.
   * Two consequences, both required by review round 6:
   *
   *   - a throwing `onWarning` can no longer abort construction before the pass has run (R6-1),
   *     because the queue is flushed with per-callback isolation *after* the pass;
   *   - no application callback runs between the pass's first physical read and its last
   *     physical erase, so a hostile handler cannot disable the backend part-way through and
   *     strand the keys the pass had not reached yet (R6-2).
   */
  onConstructed?: (store: KeyValueStore) => void;
}

const PROBE_KEY = `${STORAGE_NAMESPACE}.__probe`;

/** What has to be true before an erasure is considered finished. See `unerased`. */
type ErasureRequirement = 'destroyed' | 'removed';

/**
 * How many times construction-end quiescence may retry the backlog. See `quiesce`.
 *
 * Quiescence stops as soon as a round frees no key, so this bound is only reached by a callback
 * that keeps handing capabilities back and taking them away again, round after round. It is the
 * guard that makes construction terminate against that behaviour rather than a tuning knob:
 * whatever is still pending when it runs out stays in `pendingErasures` for the next `erase()`
 * or the next launch (D-032).
 */
const QUIESCENCE_ROUND_LIMIT = 8;

function defaultStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    // Accessing the global itself can throw when storage is blocked by policy.
    return null;
  }
}

/**
 * What the backend can actually do. Reading, inserting and deleting are three separate
 * capabilities because browsers really do offer them in combination.
 */
interface StorageCapabilities {
  readable: boolean;
  settable: boolean;
  removable: boolean;
}

const NO_CAPABILITIES: StorageCapabilities = {
  readable: false,
  settable: false,
  removable: false,
};

/**
 * Establishes what the backend can actually do, separately for reads, inserts and deletes.
 *
 * A browser that is already full, or has writes blocked by policy, can still hand back
 * everything a family saved last week. Treating a failed write probe as "no storage at all"
 * throws that away on the way in — the same mistake as discarding reads after a mid-session
 * quota failure, just at startup instead.
 *
 * Deletion is probed on its own for a sharper reason: a full quota rejects `setItem` while
 * `removeItem` keeps working, and deletion is how the store *erases* a record that must not
 * exist (a quarantined envelope carrying camera bytes, architecture §19). Folding it into the
 * insert capability meant a failed insert probe silently disabled erasure and left the
 * forbidden bytes on disk.
 */
function probeStorage(storage: StorageLike | null): StorageCapabilities {
  if (storage === null) return NO_CAPABILITIES;

  try {
    storage.getItem(PROBE_KEY);
  } catch {
    return NO_CAPABILITIES;
  }

  let settable = true;
  try {
    storage.setItem(PROBE_KEY, '1');
  } catch {
    settable = false;
  }

  let removable = true;
  try {
    // Also cleans up after a successful insert probe; a lingering probe value is harmless.
    storage.removeItem(PROBE_KEY);
  } catch {
    removable = false;
  }

  return { readable: true, settable, removable };
}

export function createKeyValueStore(options: KeyValueStoreOptions = {}): KeyValueStore {
  const emitWarning =
    options.onWarning ??
    ((message: string, cause?: unknown) => {
      console.warn(`[thrive-storage] ${message}`, cause);
    });

  const requested = options.storage === undefined ? defaultStorage() : options.storage;
  const probe = probeStorage(requested);

  /** Values written this session. Shadows `readable` for these keys only. */
  const overlay = new Map<string, string>();
  /** Keys removed this session, so a delete cannot be undone by a stale storage read. */
  const tombstones = new Set<string>();
  /**
   * Keys whose physical erasure is unfinished, and what finishing means for each.
   *
   * `destroyed` — the bytes must stop existing, so a tombstone overwrite settles it.
   * `removed` — the key *name* is itself data, so only deletion settles it (R7-3).
   *
   * The requirement is the key's, not the caller's: it is merged monotonically and never
   * downgraded by a later, weaker `erase` (R8-1, `strongest`).
   *
   * Retried on the next erasure, at construction-end quiescence, and on the next launch.
   */
  const unerased = new Map<string, ErasureRequirement>();
  const listeners = new Set<(backend: StorageBackend) => void>();

  /**
   * Warnings and backend notifications collected while an erasure is in progress.
   *
   * A supplied `onWarning` is arbitrary application code: it can call back into this store and
   * drive it into a degraded state part-way through the erasure sequence. Holding every
   * emission until the physical calls are done removes that interleaving entirely (R4-4).
   */
  let deferralDepth = 0;
  const deferredWarnings: { message: string; cause?: unknown }[] = [];
  let deferredAnnouncement = false;

  const warn = (message: string, cause?: unknown): void => {
    if (deferralDepth > 0) {
      deferredWarnings.push(cause === undefined ? { message } : { message, cause });
      return;
    }
    emitWarning(message, cause);
  };

  let readable: StorageLike | null = probe.readable ? requested : null;
  let settable: StorageLike | null = probe.settable ? requested : null;
  let removable: StorageLike | null = probe.removable ? requested : null;

  // 'read-only' describes what a *reader* of this store can rely on: existing values are
  // readable, new ones are not persisted. Deletion is deliberately not part of that signal —
  // it is a capability the store uses internally, never a promise made to the shell.
  const backendNow = (): StorageBackend => {
    if (settable !== null) return 'local-storage';
    return readable === null ? 'memory' : 'read-only';
  };

  let lastBackend = backendNow();

  /** One misbehaving subscriber must not stop the others from hearing about a degradation. */
  const notifyListeners = (next: StorageBackend): void => {
    for (const listener of [...listeners]) {
      try {
        listener(next);
      } catch {
        // Deliberately ignored: the only channel for reporting it is the one that just failed.
      }
    }
  };

  const announce = (): void => {
    const next = backendNow();
    if (next === lastBackend) return;
    lastBackend = next;
    if (deferralDepth > 0) {
      deferredAnnouncement = true;
      return;
    }
    notifyListeners(next);
  };

  /**
   * Hands the queued emissions to application code, once nothing physical is in flight.
   *
   * Each emission is isolated. A warning handler or a subscriber that throws is application code
   * misbehaving; letting it cancel the notifications queued behind it would mean one bad listener
   * could hide the fact that storage had stopped saving a child's progress. The throw is dropped
   * rather than re-raised because the only channel for reporting it is the channel that just
   * failed (review round 5, SC-1; retires the earlier "one throwing callback suppresses the rest"
   * bound).
   */
  const flushDeferred = (): void => {
    const queued = deferredWarnings.splice(0, deferredWarnings.length);
    const announcing = deferredAnnouncement;
    deferredAnnouncement = false;

    for (const entry of queued) {
      try {
        emitWarning(entry.message, entry.cause);
      } catch {
        // Deliberately ignored — see above.
      }
    }
    if (announcing) notifyListeners(backendNow());
  };

  /** The one place deferral depth is managed; `erase` and `withDeferredCallbacks` share it. */
  const deferring = <T>(fn: () => T): T => {
    deferralDepth += 1;
    try {
      return fn();
    } finally {
      deferralDepth -= 1;
      // Only the outermost scope flushes: an inner one that emitted early would reintroduce
      // exactly the interleaving the scope exists to prevent.
      if (deferralDepth === 0) flushDeferred();
    }
  };

  /**
   * Inserts stopped working. Keep reading the browser: existing data is still valid. Deletion
   * is left alone — erasing a record is still possible, and still required.
   */
  const degradeWrites = (cause: unknown): void => {
    if (settable === null) return;
    settable = null;
    warn(
      'Browser storage stopped accepting writes; this session continues in memory and nothing new will be saved.',
      cause,
    );
    announce();
  };

  /** Deletion stopped working. Tombstones keep this session honest; the bytes outlive it. */
  const degradeRemovals = (cause: unknown): void => {
    if (removable === null) return;
    removable = null;
    warn(
      'Browser storage stopped accepting deletions; removed values are hidden for this session but may still be present in the browser.',
      cause,
    );
    announce();
  };

  /** Reads stopped working too, so the browser copy is unreachable for the rest of the session. */
  const degradeReads = (cause: unknown): void => {
    if (readable === null) return;
    readable = null;
    settable = null;
    removable = null;
    warn(
      'Browser storage stopped responding to reads; continuing in memory for this session.',
      cause,
    );
    announce();
  };

  /**
   * The physical half of erasure: delete, verify, and overwrite if the record survived.
   *
   * Every call goes to `requested` — the handle the store was given — never to `readable`,
   * `settable` or `removable`. Those are conclusions about the backend, and erasure must not
   * let a stale or corrupted conclusion talk it out of trying.
   */
  const eraseFromBackend = (key: string): ErasureOutcome => {
    // No backend at all: there are no persisted bytes to destroy.
    if (requested === null) return 'removed';

    let survives: boolean;
    try {
      requested.removeItem(key);
      survives = false;
    } catch (cause) {
      survives = true;
      degradeRemovals(cause);
    }

    let residue: string | null = null;
    try {
      // Readback is the only proof that deletion did anything. Where reads are broken, a
      // deletion that did not throw is taken at its word — and one that threw is not.
      residue = requested.getItem(key);
      survives = residue != null;
    } catch (cause) {
      degradeReads(cause);
    }

    if (!survives) return 'removed';

    // Already payload-free. Rewriting the same fixed bytes would spend a write on every launch
    // for a key that deletion cannot reach; the entry is still reported as surviving, which is
    // what keeps a name-carrying key in the backlog (R7-3).
    if (residue === ERASURE_TOMBSTONE) return 'overwritten';

    try {
      requested.setItem(key, ERASURE_TOMBSTONE);
      return 'overwritten';
    } catch (cause) {
      degradeWrites(cause);
      return 'failed';
    }
  };

  /** Whether an outcome discharges what the caller asked erasure to guarantee. */
  const settles = (requirement: ErasureRequirement, outcome: ErasureOutcome): boolean =>
    requirement === 'removed' ? outcome === 'removed' : outcome !== 'failed';

  /**
   * Requirements only ever get stronger: `removed` outranks `destroyed`.
   *
   * A key's requirement is a property of the key, not of whichever call happens to reach it next.
   * Storing only the current call's requirement meant a later, weaker `erase(key)` could downgrade
   * a removal-only entry — and a re-entrant callback during the construction flush is exactly the
   * caller that does so, with the store's own decontamination pass as the earlier, stronger caller.
   * A tombstone then "settled" a key whose *name* is forbidden data, the entry left the backlog,
   * quiescence found nothing to finish and the name survived (review R8-1). Merging monotonically
   * means only a verified removal can ever discharge a key that has once required one.
   */
  const strongest = (
    recorded: ErasureRequirement | undefined,
    asked: ErasureRequirement,
  ): ErasureRequirement =>
    recorded === 'removed' || asked === 'removed' ? 'removed' : 'destroyed';

  /** Re-attempts every unfinished erasure except `justAttempted`. Returns the keys it freed. */
  const retryBacklog = (justAttempted?: string): string[] => {
    const freed: string[] = [];
    for (const [key, requirement] of [...unerased]) {
      if (key === justAttempted) continue;
      if (settles(requirement, eraseFromBackend(key))) {
        unerased.delete(key);
        freed.push(key);
      }
    }
    return freed;
  };

  const store: KeyValueStore = {
    get backend(): StorageBackend {
      return backendNow();
    },

    // Fixed for the life of the store: whether a handle was supplied, not whether it is working.
    hasPhysicalStore: requested !== null,

    subscribe(listener: (backend: StorageBackend) => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getRaw(key: string): string | null {
      if (tombstones.has(key)) return null;

      const written = overlay.get(key);
      if (written !== undefined) return written;

      if (readable === null) return null;
      try {
        const stored = readable.getItem(key);
        // A tombstone is the residue of an erasure, not a value. Reporting it as one would let
        // it be parsed, quarantined and diagnosed all over again on every launch.
        return stored === ERASURE_TOMBSTONE ? null : stored;
      } catch (cause) {
        degradeReads(cause);
        return null;
      }
    },

    readPhysical(key: string): PhysicalRead {
      if (requested === null) return { status: 'absent' };

      const attempt = (): PhysicalRead | { status: 'threw'; cause: unknown } => {
        try {
          const stored = requested.getItem(key);
          if (stored === null) return { status: 'absent' };
          // Erasure residue under a name this build chose is absence: it is payload-free by
          // construction and re-erasing it would spend a write on every launch. A caller holding
          // a name it cannot vouch for must not ask this question — see `physicalKeys`.
          return stored === ERASURE_TOMBSTONE
            ? { status: 'absent' }
            : { status: 'present', value: stored };
        } catch (cause) {
          return { status: 'threw', cause };
        }
      };

      const first = attempt();
      if (first.status !== 'threw') return first;

      // One confirmation read before concluding the bytes are unknowable. The caller's response
      // to `unreadable` is to erase (R7-1), and erasing on a single throw would let one hiccup
      // in an otherwise healthy backend destroy a family's records with no evidence at all that
      // they were contaminated. Two consecutive failures is the evidence; the cost is one extra
      // read on a path that has already failed once.
      const second = attempt();
      if (second.status !== 'threw') return second;

      degradeReads(first.cause);
      return { status: 'unreadable' };
    },

    physicalKeys(): NamespaceEnumeration {
      /**
       * No handle is **"I could not look"**, not "there is nothing here" (D-109(4)).
       *
       * `complete` exists to separate "the namespace holds nothing else" from "I could not finish
       * looking" (S1-4), and the one case where the walk never started at all was answering with
       * the first. A browser that blocks storage throws when the global is reached for, so the
       * store is built with no handle — while bytes an earlier, healthy launch wrote are still on
       * the disk. Round 1 contained the blast inside `family-erasure.ts`; this is the same disease
       * upstream of it, and every future consumer of this enumeration would have inherited it.
       *
       * The keys are still `[]`, which is honest: nothing was found, because nothing was read.
       */
      if (requested === null) return { keys: [], complete: false };
      const handle = requested;
      const found = new Set<string>();
      let complete = true;

      /**
       * How many entries to walk.
       *
       * `length` is asked twice before it is disbelieved, exactly as `readPhysical` reads twice
       * before concluding bytes are unknowable (D-032(1)): one hiccup on an otherwise healthy
       * backend must not be enough to conclude the namespace cannot be inspected, because the
       * conclusion is expensive. Two consecutive failures is the evidence.
       */
      const measure = (): number | null => {
        try {
          return handle.length;
        } catch (cause) {
          try {
            return handle.length;
          } catch {
            degradeReads(cause);
            return null;
          }
        }
      };

      const measured = measure();
      // Unmeasurable, but the names may still be reachable one at a time. Whatever comes back is
      // a salvage rather than an inventory, so the walk is incomplete however well it goes.
      const limit = measured ?? ENUMERATION_PROBE_LIMIT;
      if (measured === null) complete = false;

      /**
       * Whether any index refused to answer during the salvage walk.
       *
       * Tracked separately from `complete` because reaching a `null` used to *restore* the claim
       * of completeness: after `length` and `key(0)` both threw, `key(1) === null` was read as
       * "end of store" and the walk declared it had seen everything — while whatever `key(0)`
       * would have named was still stored and now unexamined. A null after a failed index is the
       * end of the *readable* part, which is not the same thing.
       */
      let indexRefused = false;

      for (let index = 0; index < limit; index += 1) {
        let key: string | null;
        try {
          key = handle.key(index);
        } catch (cause) {
          // One index that will not answer says nothing about the next, so the walk carries on —
          // but it can no longer claim to have seen everything.
          complete = false;
          indexRefused = true;
          degradeReads(cause);
          continue;
        }
        if (key === null) {
          // The end of a store this build could not measure. Reaching it *is* the inventory —
          // but only if every index along the way actually answered.
          if (measured === null && !indexRefused) complete = true;
          break;
        }
        if (!key.startsWith(`${STORAGE_NAMESPACE}.`)) continue;
        found.add(key);
      }

      // Snapshotted and sorted: the caller mutates the backend while walking this list.
      return { keys: [...found].sort(), complete };
    },

    setRaw(key: string, value: string): boolean {
      overlay.set(key, value);
      tombstones.delete(key);
      if (settable === null) return false;
      try {
        settable.setItem(key, value);
        return true;
      } catch (cause) {
        // Quota exceeded or storage revoked mid-session: the value stays in the overlay.
        degradeWrites(cause);
        return false;
      }
    },

    remove(key: string): void {
      overlay.delete(key);
      tombstones.add(key);
      // Deliberately independent of the insert capability: a store that cannot accept new
      // values must still be able to erase an existing one.
      if (removable === null) return;
      try {
        removable.removeItem(key);
      } catch (cause) {
        degradeRemovals(cause);
      }
    },

    erase(key: string, eraseOptions: EraseOptions = {}): ErasureOutcome {
      if (eraseOptions.retainSessionValue !== true) {
        overlay.delete(key);
        tombstones.add(key);
      }

      const requirement = strongest(
        unerased.get(key),
        eraseOptions.requireRemoval === true ? 'removed' : 'destroyed',
      );

      return deferring(() => {
        const outcome = eraseFromBackend(key);
        if (settles(requirement, outcome)) unerased.delete(key);
        else unerased.set(key, requirement);

        // A backlog exists only because a mechanism was unavailable; any later erasure is a
        // chance to find out it came back. Forbidden bytes are never left to a future launch
        // when this session can still destroy them.
        retryBacklog(key);

        return outcome;
      });
    },

    withDeferredCallbacks<T>(fn: () => T): T {
      return deferring(fn);
    },

    pendingErasures(): string[] {
      return [...unerased.keys()].sort();
    },

    keys(): string[] {
      const found = new Set<string>();
      for (const key of overlay.keys()) {
        if (key.startsWith(`${STORAGE_NAMESPACE}.`)) found.add(key);
      }
      if (readable !== null) {
        try {
          for (let index = 0; index < readable.length; index += 1) {
            const key = readable.key(index);
            if (key === null || !key.startsWith(`${STORAGE_NAMESPACE}.`)) continue;
            if (found.has(key)) continue;
            // Erasure residue is not a key anyone can read; listing it would send migrations
            // and diagnostics chasing a value that `getRaw` reports as absent.
            if (readable.getItem(key) === ERASURE_TOMBSTONE) continue;
            found.add(key);
          }
        } catch (cause) {
          degradeReads(cause);
        }
      }
      for (const key of tombstones) found.delete(key);
      return [...found].sort();
    },
  };

  /**
   * Construction-end quiescence (review R7-2).
   *
   * Holding every callback until the pass is over is what stops a hostile handler interleaving
   * with it — but it also means the *flush* is the first moment application code runs, and a
   * handler can hand back a mutation mechanism that failed moments earlier. Construction then
   * used to finish with forbidden bytes physically stored while deletion worked again, which is
   * the literal breach of what completion is supposed to mean.
   *
   * So the backlog is retried after the flush, and the retry's own flush can restore something
   * else, so it is retried again. Termination is by progress: a round that frees no key stops
   * the loop, because nothing has changed that a further identical round could exploit. A
   * callback that thrashes capabilities can keep making progress, so `QUIESCENCE_ROUND_LIMIT`
   * caps the loop outright. What is still pending when the loop ends stays in `pendingErasures`
   * and is re-attempted by the next `erase()` and by the next launch's pass (D-032).
   */
  const quiesce = (): void => {
    for (let round = 0; round < QUIESCENCE_ROUND_LIMIT; round += 1) {
      if (unerased.size === 0) return;
      // Each round is its own scope: the retries happen with nothing application-supplied able
      // to interleave, and the flush that may change the backend comes after them.
      const freed = deferring(() => retryBacklog());
      if (freed.length === 0) return;
    }
  };

  // One scope covers the probe's findings and everything `onConstructed` does, and the flush
  // afterwards isolates each callback — so construction always completes, whatever application
  // code does with the news (review R6-1, R6-2).
  deferring(() => {
    if (!probe.readable && requested !== null) {
      warn('Browser storage is unavailable; falling back to in-memory storage for this session.');
    }
    if (probe.readable && !probe.settable) {
      warn(
        'Browser storage will not accept writes; everything already saved is still readable, but nothing new will be saved this session.',
      );
    }
    options.onConstructed?.(store);
  });

  quiesce();

  return store;
}
